import React, { useEffect, useState, useRef, useCallback } from "react";
import { Card, Modal, Button, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  getVerificationImages,
  replaceFaceVerification,
} from "../../services/UserServices/UserService";
import { toast } from "react-hot-toast";
import { notifyError } from "../components/Common/Toaster";

const FaceVerificationHistory = () => {
  const [verificationImagesList, setVerificationImagesList] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [confirmShow, setConfirmShow] = useState(false);
  const [pendingReplaceId, setPendingReplaceId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [isEnd, setIsEnd] = useState(false);
  const [viewData, setViewData] = useState(null);
  const loaderRef = useRef(null);
  const navigate = useNavigate();
  const imgBaseURl = "";
  const queryParams = new URLSearchParams(window.location.search);
  const internalUserId = queryParams.get("data");
  const pageValue = queryParams.get("page");

  const breadcrumbs = [
    { label: "User Management", to: "/user-management" },
    internalUserId
      ? {
          label: "User Details",
          to: `/user-details-internal?data=${internalUserId}`,
        }
      : { label: "User Details", to: "/user-details-internal" },
    { label: "Face Verification", to: null },
  ];

  const fetchVerificationImages = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await getVerificationImages(page, limit);
      const newUsers = response.data?.data?.users || [];
      // setVerificationImagesList((prev) => [...prev, ...newItems]);
      setVerificationImagesList((prev) => {
        const uniqueUsers = newUsers.filter(
          (user) => !prev.some((p) => p._id === user._id),
        );
        return [...prev, ...uniqueUsers];
      });
      setPage((prev) => prev + 1);
    } catch (error) {
      console.log(error);
      notifyError(error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  }, [loading, isEnd, page, limit]);

  console.log(verificationImagesList, "verificationImagesList");

  useEffect(() => {
    fetchVerificationImages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchVerificationImages();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.1 },
    );
    const current = loaderRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
    };
  }, [fetchVerificationImages]);

  const handleReplace = async (faceVerificationUserId) => {
    if (!internalUserId) return;
    setReplacing(true);
    try {
      await replaceFaceVerification(internalUserId, faceVerificationUserId);
      toast.success("Face verification replaced successfully");
      setConfirmShow(false);
      setPendingReplaceId(null);
      if (pageValue === "start") {
        navigate(`/internal-user-replace-image`);
      } else {
        navigate(`/user-details-internal?data=${internalUserId}`);
      }
    } catch (error) {
      console.log(error);
      toast.error("Failed to replace face verification");
      setReplacing(false);
    }
  };

  const openConfirm = (id) => {
    setPendingReplaceId(id);
    setConfirmShow(true);
  };

  const confirmReplace = async () => {
    if (pendingReplaceId) {
      await handleReplace(pendingReplaceId);
    }
    // State updates handled in handleReplace for success/failure to manage loading state
  };

  return (
    <>
      <div className="mb-3">
        <div className="d-flex align-items-center flex-wrap gap-2 breadcrumb-elevated">
          {breadcrumbs.map((crumb, index) => (
            <React.Fragment key={index}>
              {crumb.to ? (
                <Link className="crumb-link" to={crumb.to}>
                  {crumb.label}
                </Link>
              ) : (
                <span className="crumb-active">{crumb.label}</span>
              )}
              {index !== breadcrumbs.length - 1 && (
                <span className="crumb-separator">›</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="row g-3">
        {verificationImagesList.map((item, index) => (
          <div className="col-md-4 col-lg-3 col-sm-6" key={index}>
            <div className="face-card position-relative overflow-hidden">
              <img
                src={imgBaseURl + item.verificationImage.url}
                alt={item.name}
                className="face-card-img"
                onClick={() => setViewData(item)}
              />
              <div className="face-card-overlay d-flex flex-column justify-content-between p-3 pe-none">
                <div className="d-flex justify-content-end w-100">
                  <Button
                    variant="white"
                    size="sm"
                    className="rounded-circle shadow d-flex align-items-center justify-content-center pe-auto bg-white"
                    style={{ width: 40, height: 40, padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      openConfirm(item._id);
                    }}
                    title="Replace Face Verification"
                  >
                    <i className="las la-sync text-primary fs-5"></i>
                  </Button>
                </div>
                <div
                  className="face-card-meta position-static mt-auto pe-auto cursor-pointer"
                  onClick={() => setViewData(item)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="face-card-name text-truncate">
                    {item.name}
                    {item.age ? `, ${item.age}` : ""}
                  </div>
                  <div className="face-card-sub text-truncate">
                    {item.gender
                      ? item.gender.charAt(0).toUpperCase() +
                        item.gender.slice(1)
                      : "—"}
                    {item.location ? ` (${item.location})` : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {verificationImagesList.length === 0 && !loading && (
          <div className="col-12">
            <Card className="card-elevated text-center py-5">
              <div className="text-muted">No history found</div>
            </Card>
          </div>
        )}
      </div>

      <div ref={loaderRef} className="infinite-loader text-center py-4">
        {loading && <Spinner animation="border" size="sm" />}
        {isEnd && verificationImagesList.length > 0 && (
          <div className="text-muted small">No more results</div>
        )}
      </div>

      <Modal show={confirmShow} onHide={() => setConfirmShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Replace face verification?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will replace the user's face verification with the selected
          photo. Do you want to continue?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmShow(false)}
            disabled={replacing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmReplace}
            disabled={!pendingReplaceId || !internalUserId || replacing}
          >
            {replacing ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Saving...
              </>
            ) : (
              "Replace"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Image Modal */}
      <Modal
        show={!!viewData}
        onHide={() => setViewData(null)}
        centered
        size="lg"
        contentClassName="bg-transparent border-0 shadow-none"
      >
        <Modal.Body
          className="p-0 text-center position-relative d-flex align-items-center justify-content-center"
          style={{ minHeight: "300px" }}
        >
          <button
            type="button"
            className="btn-close btn-close-white position-absolute top-0 end-0 m-3 z-index-10 bg-white opacity-100 p-2 rounded-circle"
            aria-label="Close"
            onClick={() => setViewData(null)}
            style={{ zIndex: 1056 }}
          ></button>
          {viewData && (
            <img
              src={imgBaseURl + viewData.verificationImage.url}
              alt={viewData.name}
              className="img-fluid rounded shadow-lg"
              style={{
                maxHeight: "85vh",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default FaceVerificationHistory;
